import {useLogger} from '#imports';
import type {Location} from '@malt/nuxt-common-components-module/types';
import useTypeGuards from '../composables/useTypeGuards';

export type GetSearchURLParams = {
    baseURLLink: string | null;
    query: string;
    location: Location | null;
    sourceComponent?: string; // to track the search_started Amplitude event
    customizeSearchParams?: (queryParams: URLSearchParams) => URLSearchParams;
};
const QUERY_SUGGEST_URL = '/search/api/search/autocomplete';
// LOCATION autocomplete handled by dedicated composable. Keep only query autocomplete here.

const {hasProperty} = useTypeGuards();

// Used at [CMS HOME]
export function getSearchURL({baseURLLink, query, location, sourceComponent, customizeSearchParams}: GetSearchURLParams): string {
    if (baseURLLink === null) {
        return window.location.href;
    }
    const baseURL = baseURLLink;

    const searchParams = new URLSearchParams();
    searchParams.set('q', query);

    if (location) {
        searchParams.set('as', 't');
        for (const key in location) {
            const value: Location[keyof Location] | undefined = location[<keyof Location>key];
            if (value !== null && value !== undefined) {
                if (key === 'formattedAddress' && typeof value === 'string') {
                    searchParams.set('location', value);
                } else if (
                    key === 'loc' &&
                    hasProperty(value, 'lat') &&
                    Number.isFinite(value.lat) &&
                    hasProperty(value, 'lon') &&
                    Number.isFinite(value.lon)
                ) {
                    searchParams.set('lat', value.lat.toString());
                    searchParams.set('lon', value.lon.toString());
                } else if (typeof value === 'string' || typeof value === 'boolean') {
                    searchParams.set(key, String(value));
                }
            }
        }
    }

    if (sourceComponent) {
        searchParams.set('sourceComponent', sourceComponent);
    }

    const customizedSearchParams = customizeSearchParams ? customizeSearchParams(searchParams) : searchParams;

    return `${baseURL}${baseURL.includes('?') ? '&' : '?'}${customizedSearchParams.toString()}`;
}

async function useGetQueryAutocomplete(input: string): Promise<string[]> {
    const searchParams = new URLSearchParams();
    const QUERY_MAX_LENGTH = 100;
    const sanitizedInput = input.trim().slice(0, QUERY_MAX_LENGTH);
    searchParams.set('q', sanitizedInput);

    try {
        const response = await fetch(`${QUERY_SUGGEST_URL}?${searchParams.toString()}`);

        const data = await response.json();
        return data.suggestions;
    } catch (e) {
        const logger = useLogger();
        logger.error(e);
    }
    return [];
}

export {useGetQueryAutocomplete as getQueryAutocomplete};

function createSearchHelpers() {
    return {
        getSearchURL,
        getQueryAutocomplete: useGetQueryAutocomplete,
    };
}

export default createSearchHelpers;
